import {CompetitiveGames, CompetitiveGameSummaries, League, Maps, Team} from "../../../reckon-api";
import Button from "@mui/material/Button";
import React, {useEffect, useState} from "react";
import {capitalize, Dialog, IconButton, Slide, Stack, Typography, useTheme, Fade} from "@mui/material";
import FilterSet from "../Filterset";
import {targets, useReferentialContext} from "../../../Context";
import {TransitionProps} from "@mui/material/transitions";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import {Close} from "@mui/icons-material";
import WindowContainer from "../../layout/WindowContainer";
import SpecificGameSelector from "./SpecificGameSelector";
import Box from "@mui/material/Box";
import TeamIcon from "../../Icons/TeamIcon";
import AgentIcon from "../../Icons/AgentIcon";
import {NeonAppBar} from "../../../NeonComponents/NeonAppBar";
import {NeonCard, NeonCardContent, NeonCardHeader} from "../../../NeonComponents/NeonCards";
import {
    NeonScoreBadge,
    NeonLabelBadge,
} from "../../../NeonComponents/NeonTable";
import { getNeonColor } from "../../../theme";
import dayjs from "dayjs";

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<unknown>;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

function QuickGamePresentationLine(props: {game: CompetitiveGames}){
    return <Stack direction={'row'} spacing={1}>
        <TeamIcon team_name={props.game.team1}/>
        {props.game.team1_agents.split(',').map((agent_name: string) => <AgentIcon agent_name={agent_name}/>)}
        <Typography variant={'h4'}>{props.game.score_team1} - {capitalize(props.game.map)} - {props.game.score_team2}</Typography>
        {props.game.team2_agents.split(',').map((agent_name: string) => <AgentIcon agent_name={agent_name}/>)}
        <TeamIcon team_name={props.game.team2}/>
    </Stack>
}

function QuickGamesPresentationLine(props: {games: CompetitiveGames[], team: Team|null}){
    return props.team ? <Stack direction={'row'} spacing={1}>
        {
            props.games.map(g => {
                const vs = g.team1 === props.team?.id ? g.team2 : g.team1;
                const team_score = g.team1 === props.team?.id ? g.score_team1 : g.score_team2;
                const vs_team_score = g.team1 === props.team?.id ? g.score_team2 : g.score_team1;
                return <Stack direction={'column'}>
                <Stack direction={'row'} sx={{width:'150px'}}>
                    <Typography variant={'h5'}>{team_score} - {vs_team_score}</Typography>
                    <TeamIcon team_name={vs}/>
                </Stack>
                    <Typography variant={'h4'}>{capitalize(g.map)}</Typography>
                </Stack>
            })
        }
    </Stack> : <></>
}

export default function SpecificGameSelectorDialog(props: {game: CompetitiveGames | null, setGame: (nv: CompetitiveGames) => void, target: targets, team:Team|null, setTeam: (nv: Team|null) => void}){
    const [dialogOpen, setDialogOpen] = useState(props.game === null);
    const [paramObject, setParamObject] = useState({ordering: ['-date']} as any);
    const theme = useTheme();
    // const [leagues, setLeagues] = useState([] as League[]);
    const handleClose = () => {
        setDialogOpen(false);
    };

    return <Stack direction={'row'} sx={{width:'100%', alignItems:'center', justifyContent:'space-between'}}>
    <Button onClick={() => setDialogOpen(true)} sx={{width:'200px'}} variant={'outlined'}>Change Game</Button>
        <Dialog 
            fullScreen 
            open={dialogOpen} 
            sx={{width:'100%', height:'100%'}} 
            onClose={handleClose} 
            TransitionComponent={Transition}
        >
            <NeonAppBar position="relative" >
              <Toolbar>
                <IconButton
                  edge="start"
                  color="inherit"
                  onClick={() => setDialogOpen(false)}
                  aria-label="close"
                >
                  <Close onClick={() => setDialogOpen(false)}/>
                </IconButton>
                <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                  Game Select
                </Typography>
              </Toolbar>
            </NeonAppBar>
            <WindowContainer 
                spacing={2} 
                sx={{height:'90%', width:'100%', cursor: 'pointer'}}
                onClick={(e: React.MouseEvent) => {
                    // Close dialog if clicking directly on the background (not on a child element that stopped propagation)
                    if (e.target === e.currentTarget) {
                        setDialogOpen(false);
                    }
                }}
            >
                <Box sx={{ width: '100%', mb: 2 }} onClick={(e) => e.stopPropagation()}>
                    <FilterSet paramObject={paramObject} setParamObject={setParamObject} target={props.target}
                               team={props.team} setTeam={props.setTeam}/>
                </Box>
                <Stack 
                    direction="row" 
                    spacing={2} 
                    sx={{ 
                        flex: 1, 
                        width: '100%', 
                        minHeight: 0,
                        alignItems: 'stretch',
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <Box sx={{ flex: 7, minWidth: 0, display: 'flex' }}>
                        {props.team && paramObject.team1 ? <SpecificGameSelector team={props.team} paramObject={paramObject} updateSelection={props.setGame} single={true}/> : <></>}
                    </Box>
                    <Box sx={{ flex: 5, minWidth: 0, display: 'flex' }}>
                        {props.game && <GameRatingsDetails game={props.game}/>}
                    </Box>
                </Stack>
            </WindowContainer>
        </Dialog>
        {props.game ? <QuickGamePresentationLine game={props.game}/> : <Typography variant={'h4'}>No game selected</Typography>}
        </Stack>
}

export function MultipleGameSelectorDialog(props: {
    games: CompetitiveGames[],
    setGames: React.Dispatch<CompetitiveGames[]>,
    target: targets,
    team:Team|null,
    setTeam: (nv: Team|null) => void,
    sameMap: boolean,
}){
    const [dialogOpen, setDialogOpen] = useState(false);
    const [paramObject, setParamObject] = useState({ordering: ['-date']} as any);
    // const [leagues, setLeagues] = useState([] as League[]);
    const handleClose = () => {
        setDialogOpen(false);
    };

    return <Stack direction={'row'} sx={{width:'100%', alignItems:'center', justifyContent:'space-between'}}>
        <Stack sx={{display:'flex', alignItems:'center', justifyContent:'center'}} spacing={1} direction={'row'}>
            {props.team ? <TeamIcon team_name={props.team.name}/> : <Typography>No team select</Typography>}
            <Button onClick={() => setDialogOpen(true)} sx={{width:'200px'}} variant={'outlined'}>Change Game Set</Button>
        </Stack>
        <QuickGamesPresentationLine games={props.games} team={props.team}/>
        <Dialog 
            fullScreen 
            open={dialogOpen} 
            sx={{width:'100%', height:'100%'}} 
            onClose={handleClose} 
            TransitionComponent={Transition}
        >
            <AppBar sx={{ position: 'relative' }}>
              <Toolbar>
                <IconButton
                  edge="start"
                  color="inherit"
                  onClick={() => setDialogOpen(false)}
                  aria-label="close"
                >
                  <Close onClick={() => setDialogOpen(false)}/>
                </IconButton>
                <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                  Game Select
                </Typography>
              </Toolbar>
            </AppBar>
            <WindowContainer 
                spacing={2} 
                sx={{height:'90%', width:'100%', cursor: 'pointer'}}
                onClick={(e: React.MouseEvent) => {
                    if (e.target === e.currentTarget) {
                        setDialogOpen(false);
                    }
                }}
            >
                <Box onClick={(e) => e.stopPropagation()}>
                    <FilterSet paramObject={paramObject} setParamObject={setParamObject} target={props.target}
                               // leagues={leagues}
                               // setLeagues={setLeagues}
                               team={props.team} setTeam={props.setTeam}/>
                </Box>
                <Box onClick={(e) => e.stopPropagation()} sx={{ flex: 1, minHeight: 0, width: '100%' }}>
                    {props.team && paramObject.team1 ? <SpecificGameSelector single={false} team={props.team} paramObject={paramObject} updateSelection={props.setGames} sameMap={props.sameMap}/> : <></>}
                </Box>
            </WindowContainer>
        </Dialog>
        </Stack>
}

// Column configuration for stats table - using flex for responsive layout
const STAT_COLUMNS = {
    player: { flex: 2, minWidth: '90px' },
    agent: { flex: 0, width: '32px' },
    rating: { flex: 1, minWidth: '40px' },
    acs: { flex: 1, minWidth: '35px' },
    kda: { flex: 1.5, minWidth: '65px' },
    kast: { flex: 1, minWidth: '40px' },
    hs: { flex: 1, minWidth: '40px' },
    fk: { flex: 0.5, minWidth: '28px' },
    fd: { flex: 0.5, minWidth: '28px' },
} as const;

// Stat cell with color coding
const StatCell = ({ value, format, highlight, flex, minWidth }: { 
    value: number | null | undefined; 
    format: 'rating' | 'integer' | 'percent';
    highlight?: 'high' | 'low' | 'none';
    flex: number;
    minWidth: string;
}) => {
    let displayValue = '-';
    let color = 'rgba(255,255,255,0.7)';
    
    if (value !== null && value !== undefined) {
        switch (format) {
            case 'rating':
                displayValue = value.toFixed(2);
                if (value >= 1.2) color = '#2ecc71';
                else if (value >= 1.0) color = 'rgba(255,255,255,0.85)';
                else if (value >= 0.8) color = '#f39c12';
                else color = '#e74c3c';
                break;
            case 'integer':
                displayValue = value.toFixed(0);
                break;
            case 'percent':
                displayValue = `${(value * 100).toFixed(0)}%`;
                break;
        }
    }

    if (highlight === 'high') color = '#2ecc71';
    if (highlight === 'low') color = '#e74c3c';

    return (
        <Box sx={{ 
            flex,
            minWidth,
            display: 'flex', 
            justifyContent: 'center',
            alignItems: 'center',
        }}>
            <Typography sx={{ 
                fontSize: '0.75rem', 
                fontWeight: format === 'rating' ? 700 : 500,
                color,
                fontFamily: 'monospace',
            }}>
                {displayValue}
            </Typography>
        </Box>
    );
};

// Player row component
const PlayerStatRow = ({ summary, index, isTopFragger }: { 
    summary: CompetitiveGameSummaries; 
    index: number;
    isTopFragger?: boolean;
}) => (
    <Fade in timeout={100 + index * 30}>
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            padding: '5px 8px',
            borderRadius: '4px',
            background: isTopFragger 
                ? 'linear-gradient(90deg, rgba(46, 204, 113, 0.18) 0%, rgba(46, 204, 113, 0.05) 100%)'
                : 'rgba(255,255,255,0.02)',
            borderLeft: isTopFragger ? '3px solid #2ecc71' : '3px solid transparent',
            boxShadow: isTopFragger ? 'inset 0 0 15px rgba(46, 204, 113, 0.1)' : 'none',
            transition: 'all 0.15s ease',
            '&:hover': {
                background: isTopFragger 
                    ? 'linear-gradient(90deg, rgba(46, 204, 113, 0.25) 0%, rgba(46, 204, 113, 0.08) 100%)'
                    : 'rgba(255,255,255,0.06)',
            }
        }}>
            {/* Player name */}
            <Box sx={{ flex: STAT_COLUMNS.player.flex, minWidth: STAT_COLUMNS.player.minWidth, display: 'flex', alignItems: 'center' }}>
                <Typography sx={{ 
                    fontSize: '0.7rem', 
                    fontWeight: 600,
                    color: 'rgba(255,255,255,0.9)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                }}>
                    {summary.player}
                </Typography>
            </Box>
            
            {/* Agent */}
            <Box sx={{ flex: STAT_COLUMNS.agent.flex, width: STAT_COLUMNS.agent.width, display: 'flex', justifyContent: 'center' }}>
                <AgentIcon agent_name={summary.agent} sx={{ width: 22, height: 22 }}/>
            </Box>
            
            {/* Stats */}
            <StatCell value={summary.rating_hltv} format="rating" flex={STAT_COLUMNS.rating.flex} minWidth={STAT_COLUMNS.rating.minWidth} />
            <StatCell value={summary.acs} format="integer" flex={STAT_COLUMNS.acs.flex} minWidth={STAT_COLUMNS.acs.minWidth} />
            
            {/* KDA */}
            <Box sx={{ 
                flex: STAT_COLUMNS.kda.flex,
                minWidth: STAT_COLUMNS.kda.minWidth, 
                display: 'flex', 
                justifyContent: 'center',
                alignItems: 'center',
                gap: 0.25,
            }}>
                <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#2ecc71' }}>
                    {summary.kills}
                </Typography>
                <Typography sx={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.3)' }}>/</Typography>
                <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#e74c3c' }}>
                    {summary.deaths}
                </Typography>
                <Typography sx={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.3)' }}>/</Typography>
                <Typography sx={{ fontSize: '0.65rem', fontWeight: 500, color: 'rgba(255,255,255,0.6)' }}>
                    {summary.assists}
                </Typography>
            </Box>
            
            <StatCell value={summary.kast} format="percent" flex={STAT_COLUMNS.kast.flex} minWidth={STAT_COLUMNS.kast.minWidth} />
            <StatCell value={summary.headshot_percent} format="percent" flex={STAT_COLUMNS.hs.flex} minWidth={STAT_COLUMNS.hs.minWidth} />
            <StatCell value={summary.first_kills} format="integer" highlight="high" flex={STAT_COLUMNS.fk.flex} minWidth={STAT_COLUMNS.fk.minWidth} />
            <StatCell value={summary.first_deaths} format="integer" highlight="low" flex={STAT_COLUMNS.fd.flex} minWidth={STAT_COLUMNS.fd.minWidth} />
        </Box>
    </Fade>
);

// Team section component
const TeamSection = ({ 
    teamId,
    teamDisplayName, 
    players, 
    score, 
    isWinner,
    startIndex = 0 
}: { 
    teamId: string;
    teamDisplayName: string; 
    players: CompetitiveGameSummaries[];
    score: number;
    isWinner: boolean;
    startIndex?: number;
}) => {
    const theme = useTheme();
    const neonColor = getNeonColor(theme);
    
    // Find top fragger (highest rating)
    const topFraggerIndex = players.length > 0 
        ? players.reduce((maxIdx, player, idx, arr) => 
            (player.rating_hltv || 0) > (arr[maxIdx]?.rating_hltv || 0) ? idx : maxIdx, 0)
        : -1;

    return (
        <Box sx={{ width: '100%' }}>
            {/* Team header */}
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '6px 10px',
                marginBottom: '4px',
                borderRadius: '6px',
                background: isWinner 
                    ? 'linear-gradient(90deg, rgba(46, 204, 113, 0.15) 0%, rgba(46, 204, 113, 0.03) 100%)'
                    : 'linear-gradient(90deg, rgba(231, 76, 60, 0.15) 0%, rgba(231, 76, 60, 0.03) 100%)',
                borderLeft: `3px solid ${isWinner ? '#2ecc71' : '#e74c3c'}`,
                boxShadow: isWinner 
                    ? 'inset 0 0 15px rgba(46, 204, 113, 0.08)'
                    : 'inset 0 0 15px rgba(231, 76, 60, 0.08)',
            }}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <TeamIcon team_name={teamId} sx={{ width: 24, height: 24 }}/>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
                        {teamDisplayName}
                    </Typography>
                </Stack>
                <NeonScoreBadge score={score} variant={isWinner ? 'win' : 'loss'} size="small" />
            </Box>

            {/* Column headers - match player row padding (8px + 3px border = 11px left) */}
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                padding: '2px 8px 2px 11px', // 11px left to account for 3px border + 8px padding in player rows
                marginBottom: '2px',
            }}>
                <Typography sx={{ flex: STAT_COLUMNS.player.flex, minWidth: STAT_COLUMNS.player.minWidth, fontSize: '0.55rem', color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Player
                </Typography>
                <Box sx={{ flex: STAT_COLUMNS.agent.flex, width: STAT_COLUMNS.agent.width, display: 'flex', justifyContent: 'center' }} />
                <Typography sx={{ flex: STAT_COLUMNS.rating.flex, minWidth: STAT_COLUMNS.rating.minWidth, fontSize: '0.55rem', color: 'rgba(255,255,255,0.35)', textAlign: 'center', textTransform: 'uppercase' }}>
                    R.
                </Typography>
                <Typography sx={{ flex: STAT_COLUMNS.acs.flex, minWidth: STAT_COLUMNS.acs.minWidth, fontSize: '0.55rem', color: 'rgba(255,255,255,0.35)', textAlign: 'center', textTransform: 'uppercase' }}>
                    ACS
                </Typography>
                <Typography sx={{ flex: STAT_COLUMNS.kda.flex, minWidth: STAT_COLUMNS.kda.minWidth, fontSize: '0.55rem', color: 'rgba(255,255,255,0.35)', textAlign: 'center', textTransform: 'uppercase' }}>
                    K/D/A
                </Typography>
                <Typography sx={{ flex: STAT_COLUMNS.kast.flex, minWidth: STAT_COLUMNS.kast.minWidth, fontSize: '0.55rem', color: 'rgba(255,255,255,0.35)', textAlign: 'center', textTransform: 'uppercase' }}>
                    KAST
                </Typography>
                <Typography sx={{ flex: STAT_COLUMNS.hs.flex, minWidth: STAT_COLUMNS.hs.minWidth, fontSize: '0.55rem', color: 'rgba(255,255,255,0.35)', textAlign: 'center', textTransform: 'uppercase' }}>
                    HS%
                </Typography>
                <Typography sx={{ flex: STAT_COLUMNS.fk.flex, minWidth: STAT_COLUMNS.fk.minWidth, fontSize: '0.55rem', color: 'rgba(255,255,255,0.35)', textAlign: 'center', textTransform: 'uppercase' }}>
                    FK
                </Typography>
                <Typography sx={{ flex: STAT_COLUMNS.fd.flex, minWidth: STAT_COLUMNS.fd.minWidth, fontSize: '0.55rem', color: 'rgba(255,255,255,0.35)', textAlign: 'center', textTransform: 'uppercase' }}>
                    FD
                </Typography>
            </Box>

            {/* Player rows */}
            <Stack spacing={0.25}>
                {players.map((summary, idx) => (
                    <PlayerStatRow 
                        key={summary.id || idx}
                        summary={summary} 
                        index={startIndex + idx}
                        isTopFragger={idx === topFraggerIndex}
                    />
                ))}
            </Stack>
        </Box>
    );
};

export function GameRatingsDetails(props: {game: CompetitiveGames}){
    const ctx = useReferentialContext();
    const theme = useTheme();
    const neonColor = getNeonColor(theme);
    const [gameSummaries, setGameSummaries] = useState([] as CompetitiveGameSummaries[]);

    // Helper to get team display name from ID
    const getTeamName = (teamId: string): string => {
        const team = ctx.teamsReferential.find(t => t.id === teamId);
        return team?.name || teamId;
    };

    useEffect(() => {
        setGameSummaries([]);
        ctx.reckonApi.competitiveGameSummaries.list({
            additional_filters: {game_id: props.game.id}, 
            ordering: ['team1']
        }).then((r) => setGameSummaries(r))
    }, [props.game]);

    const team1Won = props.game.winner === props.game.team1;
    
    // Determine which team a player is on using win field and game winner
    // If player won and team1 won -> player is on team1
    // If player lost and team1 won -> player is on team2
    const team1Players = gameSummaries.filter(s => s.win === team1Won);
    const team2Players = gameSummaries.filter(s => s.win !== team1Won);
    
    const team1Name = getTeamName(props.game.team1);
    const team2Name = getTeamName(props.game.team2);

    return (
        <NeonCard sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Game header - compact */}
            <Box sx={{
                padding: '10px 14px',
                borderBottom: '1px solid rgba(255,255,255,0.06)',
            }}>
                {/* League and date */}
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                    <NeonLabelBadge label={props.game.league} maxWidth="120px" />
                    <Typography sx={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.4)' }}>
                        {props.game.date ? dayjs(props.game.date).format("MMM D, YYYY") : ''}
                    </Typography>
                </Stack>

                {/* Match summary - compact */}
                <Stack direction="row" alignItems="center" justifyContent="center" spacing={2.5}>
                    <Stack alignItems="center" spacing={0.25}>
                        <TeamIcon team_name={props.game.team1} sx={{ width: 32, height: 32 }}/>
                        <Typography sx={{ 
                            fontSize: '0.65rem', 
                            fontWeight: 600,
                            color: team1Won ? '#2ecc71' : 'rgba(255,255,255,0.5)',
                            maxWidth: '80px',
                            textAlign: 'center',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}>
                            {team1Name}
                        </Typography>
                    </Stack>

                    <Stack alignItems="center" spacing={0.25}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <NeonScoreBadge 
                                score={props.game.score_team1} 
                                variant={team1Won ? 'win' : 'loss'}
                                size="small"
                            />
                            <Typography sx={{ color: 'rgba(255,255,255,0.25)', fontSize: '1rem', fontWeight: 300 }}>:</Typography>
                            <NeonScoreBadge 
                                score={props.game.score_team2} 
                                variant={!team1Won ? 'win' : 'loss'}
                                size="small"
                            />
                        </Stack>
                        <Typography sx={{ 
                            fontSize: '0.75rem', 
                            fontWeight: 700,
                            color: neonColor,
                            textTransform: 'uppercase',
                            letterSpacing: '1px',
                        }}>
                            {capitalize(props.game.map)}
                        </Typography>
                    </Stack>

                    <Stack alignItems="center" spacing={0.25}>
                        <TeamIcon team_name={props.game.team2} sx={{ width: 32, height: 32 }}/>
                        <Typography sx={{ 
                            fontSize: '0.65rem', 
                            fontWeight: 600,
                            color: !team1Won ? '#2ecc71' : 'rgba(255,255,255,0.5)',
                            maxWidth: '80px',
                            textAlign: 'center',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}>
                            {team2Name}
                        </Typography>
                    </Stack>
                </Stack>
            </Box>

            <NeonCardContent sx={{ width: '100%', padding: '10px 12px', flex: 1, overflow: 'auto' }}>
                <Stack spacing={1.5} sx={{ height: '100%' }}>
                    {/* Team 1 */}
                    {team1Players.length > 0 && (
                        <TeamSection 
                            teamId={props.game.team1}
                            teamDisplayName={team1Name}
                            players={team1Players}
                            score={props.game.score_team1}
                            isWinner={team1Won}
                            startIndex={0}
                        />
                    )}

                    {/* Divider */}
                    {team1Players.length > 0 && team2Players.length > 0 && (
                        <Box sx={{ 
                            height: '1px', 
                            background: `linear-gradient(90deg, transparent, ${neonColor}25, transparent)`,
                        }} />
                    )}

                    {/* Team 2 */}
                    {team2Players.length > 0 && (
                        <TeamSection 
                            teamId={props.game.team2}
                            teamDisplayName={team2Name}
                            players={team2Players}
                            score={props.game.score_team2}
                            isWinner={!team1Won}
                            startIndex={5}
                        />
                    )}

                    {/* Empty state */}
                    {gameSummaries.length === 0 && (
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '200px',
                        }}>
                            <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontStyle: 'italic' }}>
                                Loading player stats...
                            </Typography>
                        </Box>
                    )}
                </Stack>
            </NeonCardContent>
        </NeonCard>
    );
}